import { css } from '@emotion/core'
import * as React from 'react'
import { Plus } from 'react-feather'
import { default as Spinner } from './Loader'

import { breakpoints, palette } from 'main/ui'
import { UnreachableCaseError } from 'main/util/ts'
import {
  ButtonAlternate,
  ButtonDanger,
  ButtonOutline,
  ButtonRegular,
  ButtonRounded,
  ButtonSoftDisabled,
} from './Button.styles'

interface IProps {
  kind: 'regular' | 'outline' | 'soft_disabled' | 'alternate' | 'danger' | 'rounded'
  disabled: boolean
  full: boolean
  pending?: boolean
  withPlus?: boolean
  children: React.ReactNode
  type?: 'submit' | 'reset' | 'button'
  small?: boolean
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
}

function Button(props: IProps) {
  const styles = css`
    display: flex;
    justify-content: center;
    span:nth-child(1) {
      visibility: hidden;
    }
    span:nth-child(2) {
      position: absolute;
    }
  `

  const colorSpinner = props.kind === 'outline' ? palette.almostBlack : palette.white

  const spinner = (color: string) => (
    <span css={styles}>
      <span>{props.children}</span>
      <span>
        <Spinner color={color} />
      </span>
    </span>
  )

  const mediaQuery = `@media (max-width: ${breakpoints.sm}px)`
  const withPlus = () => (
    <span css={{ display: 'flex', justifyContent: 'flex-start' }}>
      <span
        css={{
          flexGrow: 1,
          textAlign: 'start',
          position: 'fixed',
          [mediaQuery]: { position: 'absolute' },
        }}
      >
        <Plus />
      </span>
      <span css={{ flexGrow: 3 }}>{props.children}</span>
    </span>
  )

  const adjustedProps = props.pending
    ? { ...props, children: spinner(colorSpinner), onClick: () => null }
    : props.withPlus
    ? { ...props, children: withPlus() }
    : props

  switch (props.kind) {
    case 'regular':
      return <ButtonRegular {...adjustedProps} />
    case 'outline':
      return <ButtonOutline {...adjustedProps} />
    case 'soft_disabled':
      return <ButtonSoftDisabled {...adjustedProps} />
    case 'alternate':
      return <ButtonAlternate {...adjustedProps} />
    case 'danger':
      return <ButtonDanger {...adjustedProps} />
    case 'rounded':
      return <ButtonRounded {...adjustedProps} />
    default:
      throw new UnreachableCaseError(props.kind)
  }
}

Button.defaultProps = {
  disabled: false,
  full: false,
  kind: 'regular',
  small: false,
}

export default React.memo(Button)
